/* oxlint-disable max-lines-per-function */
import { Text } from 'Text';
import { useTranslation } from 'react-i18next';
import { type RefObject, type FC, useRef, useCallback, createRef, memo, useMemo } from 'react';
import clsx from 'clsx';
import { useIntersectionObserver, useIntersectionObservers } from 'useIntersectionObserver';
import { useTargetBrandConfig } from 'FrontRoyalAngular/useTargetBrandConfig';
import { useEventLogger, useOfflineModeManager } from 'FrontRoyalAngular';
import debounce from 'Utils/debounce';
import { StudentSpotlightVideo } from './StudentSpotlightVideo';
import { ScrollArrowButton } from './ScrollArrowButton';
import { type StudentSpotlightCarouselProps } from './StudentSpotlight.types';

type StreamIndexRefs = {
    [index: string]: RefObject<HTMLDivElement | null>;
};

const visibleStreamsIoOptions = {
    rootMargin: '150px 0px 150px 0px',
    threshold: 0.95,
};

export const StudentSpotlightCarousel: FC<StudentSpotlightCarouselProps> = memo(
    ({
        streamIds,
        autoplayFirstPreview = true,
        playPreviewOnHover = true,
        scalePreviewOnHover = true,
        showInstitutionLogo = false,
    }) => {
        const { t } = useTranslation('back_royal');
        const { brandNameShort } = useTargetBrandConfig();
        const EventLogger = useEventLogger();
        const offlineModeManager = useOfflineModeManager();

        const logScroll = useCallback(() => {
            EventLogger.log('student-spotlight:carousel_scrolled');
        }, [EventLogger]);

        const debouncedLogScroll = useMemo(() => debounce(logScroll, 200), [logScroll]);

        const containerRef = useRef<HTMLDivElement>(null);
        const carouselRef = useRef<HTMLDivElement>(null);
        const scrollRoot = document.querySelector('#app-main-container');
        const verticalIoOptions = useMemo(
            () => ({ root: scrollRoot, rootMargin: '200px 0px 400px 0px' }),
            [scrollRoot],
        );
        const isVerticallyCloseToVisible = useIntersectionObserver(containerRef, verticalIoOptions);

        // Rebuilding a player costs a fetch and a visible reload, so the gate only defers the
        // first load. It never clears, and never tears a loaded player down.
        const everVerticallyCloseRef = useRef(false);
        if (isVerticallyCloseToVisible) everVerticallyCloseRef.current = true;

        const streamIndexRefs = useRef<StreamIndexRefs>(
            streamIds.reduce<StreamIndexRefs>((acc, _, index) => {
                acc[index] = createRef();
                return acc;
            }, {}),
        );

        const refsList = useMemo(() => Object.values(streamIndexRefs.current), []);

        const visibleStreams = useIntersectionObservers(refsList, containerRef, visibleStreamsIoOptions);

        // Mid-scroll no card clears the intersection threshold, and both searches return -1.
        // Letting that through collapses the load window onto cards 0 and 1, wherever the
        // student has actually scrolled to.
        const lastVisibleRange = useRef({ leftmost: 0, rightmost: 0 });
        const leftmost = visibleStreams.findIndex(visible => visible);
        const rightmost = visibleStreams.findLastIndex(visible => visible);
        if (leftmost !== -1 && rightmost !== -1) lastVisibleRange.current = { leftmost, rightmost };
        const { leftmost: leftmostVisibleStreamIndex, rightmost: rightmostVisibleStreamIndex } =
            lastVisibleRange.current;

        const onPress = useCallback(
            (direction: 'left' | 'right') => () => {
                const carousel = carouselRef.current;
                const container = containerRef.current;
                if (!carousel || !container) return;

                const parentWidth = container.clientWidth;

                const targetIndex =
                    direction === 'left' ? leftmostVisibleStreamIndex - 1 : rightmostVisibleStreamIndex + 1;

                const nextStream = streamIndexRefs.current[targetIndex];
                if (!nextStream?.current) return;

                const itemLeft = nextStream.current.offsetLeft;
                const itemRight = itemLeft + nextStream.current.offsetWidth;

                const scroll = direction === 'left' ? itemLeft : itemRight - parentWidth;

                logScroll();

                carousel.scrollTo({
                    left: scroll,
                    behavior: 'smooth',
                });
            },
            [leftmostVisibleStreamIndex, logScroll, rightmostVisibleStreamIndex],
        );

        const getShouldLoad = (i: number) =>
            // If within the range of visible streams +/- 2
            // i.e if 3,4,5 are visible, then 1-7 are loaded
            leftmostVisibleStreamIndex - 2 <= i &&
            i <= rightmostVisibleStreamIndex + 2 &&
            everVerticallyCloseRef.current;

        if (offlineModeManager.inOfflineMode) return null;

        return (
            <div className="relative -mx-2.5 overflow-hidden sm:-mx-7.5 md:mx-0" ref={containerRef}>
                <Text as="h2" weight="normal" className="mb-3.5 ms-[22px] sm:ms-[42px] md:ms-3">
                    {t('featured_students.featured_students.branded_student_spotlight', { brandNameShort })}
                </Text>
                <ScrollArrowButton direction="left" hide={visibleStreams[0]} onPress={onPress('left')} />
                <div
                    ref={carouselRef}
                    onScroll={debouncedLogScroll}
                    className={clsx(
                        'hide-scrollbar md:space-between flex flex-row justify-start',
                        '-my-15 gap-x-7.5 overflow-x-auto overflow-y-visible p-15',
                        '-ml-12.5 -mr-15 rtl:-ml-15 rtl:-mr-12.5',
                        'sm:-mx-7.5 rtl:sm:-mx-7.5',
                        'md:-mx-15 rtl:md:-mx-15',
                    )}
                >
                    {streamIds.map((streamId, i) => (
                        <StudentSpotlightVideo
                            key={streamId}
                            streamId={streamId}
                            autoplayPreview={autoplayFirstPreview && i === 0}
                            playPreviewOnHover={playPreviewOnHover}
                            scalePreviewOnHover={scalePreviewOnHover}
                            showInstitutionLogo={showInstitutionLogo}
                            videoContainerRef={streamIndexRefs.current[i]}
                            shouldLoad={getShouldLoad(i)}
                            offScreen={!isVerticallyCloseToVisible}
                        />
                    ))}
                </div>
                <ScrollArrowButton
                    direction="right"
                    hide={visibleStreams[visibleStreams.length - 1]}
                    onPress={onPress('right')}
                />
            </div>
        );
    },
);
